import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Outlet } from "react-router-dom";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import Sidebar from "../components/Sidebar";

function formatRelativeTime(value) {
  if (!value) return "—";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin <= 0) return "just now";
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;

  const diffHours = Math.round(diffMin / 60);
  if (diffHours === 1) return "1 hour ago";
  return `${diffHours} hours ago`;
}

export default function DashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Mobile sidebar state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Global AI status
  const [aiLastUpdated, setAiLastUpdated] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRefreshToken, setAiRefreshToken] = useState(0);

  // Children (Clients, Deals, Tasks, AI, etc.) will call this
  const handleAiStatusChange = useCallback(({ lastUpdated, loading }) => {
    if (typeof loading === "boolean") {
      setAiLoading(loading);
    }
    if (lastUpdated) {
      setAiLastUpdated(lastUpdated);
    }
  }, []);

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Sidebar mobileOpen={mobileOpen} onMobileClose={handleMobileClose} />

      {/* MOBILE FIXED HEADER - Glassmorphism */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // Glassmorphism effect
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            boxShadow: `0 4px 30px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
            {/* LEFT: Menu + Logo */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                onClick={handleMobileToggle}
                sx={{
                  color: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
              <BoltRoundedIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                AgentBlayna
              </Typography>
            </Stack>

            {/* RIGHT: AI Status + Refresh */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                label="AI"
                size="small"
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              />
              <Tooltip title="Refresh AI">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={aiLoading}
                    onClick={() => setAiRefreshToken((t) => t + 1)}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      },
                    }}
                  >
                    <RefreshRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        )}

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          // Top padding accounts for fixed mobile header (~64px) + breathing room
          pt: isMobile ? "68px" : 3,
          px: isMobile ? 1.5 : 3,
          pb: 3,
          // Prevent child elements from overflowing on narrow screens
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
          {/* DESKTOP HEADER - Original design */}
          {!isMobile && (
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2.5,
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                boxShadow: "0 2px 6px rgba(0,0,0,.05)",
              }}
            >
              {/* LEFT SIDE: TITLE + SUBTEXT */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BoltRoundedIcon sx={{ color: "primary.main" }} />
                  <Typography variant="h5" fontWeight={800}>
                    AgentBlayna — Realtor Assistant
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Smarter Real Estate Starts Here
                </Typography>
              </Box>

              {/* RIGHT SIDE: STATUS + REFRESH */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label="AI Active"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  }}
                />

                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{ mx: 1, borderColor: theme.palette.divider }}
                />

                <Typography variant="caption" color="text.secondary">
                  Last updated:{" "}
                  {aiLoading ? "updating…" : formatRelativeTime(aiLastUpdated)}
                </Typography>

                <Tooltip title="Refresh by AI">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={aiLoading}
                      onClick={() => setAiRefreshToken((t) => t + 1)}
                    >
                      <RefreshRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Paper>
          )}

        {/* CHILD ROUTES */}
        <Outlet
          context={{
            onAiStatusChange: handleAiStatusChange,
            aiRefreshToken,
          }}
        />
      </Box>
    </Box>
  );
}
